import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, Zap, Gauge, Timer } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CarGallery from "@/components/CarGallery";
import SEO from "@/components/SEO";
import { cars } from "@/data/cars";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1] as const;

const CarDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const car = cars.find((c) => c.slug === slug);
  const { t } = useLanguage();

  if (!car) return <Navigate to="/drive/ibiza" replace />;

  const getSpecValue = (label: string) =>
    car.specs.find((s) => s.label === label)?.value || "—";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title={`${car.name} — Arzani Drive Ibiza`}
        description={(car.description || `Rent the ${car.name} through Arzani Drive in Ibiza.`).slice(0, 155)}
        path={`/drive/ibiza/${car.slug}`}
        image={car.image || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: car.name,
          description: car.description,
          image: car.image || undefined,
          brand: { "@type": "Brand", name: "Arzani Drive" },
          offers: { "@type": "Offer", price: car.price, priceCurrency: "EUR" },
        }}
      />
      <Navigation />

      {/* Hero Image */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {car.gallery && car.gallery.length > 0 ? (
            <CarGallery images={car.gallery} name={car.name} />
          ) : car.image ? (
            <img
              src={car.image}
              alt={car.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-muted" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-foreground/30 pointer-events-none" />
        </div>
        <div className="relative z-10 container-luxury pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1.2, ease }}
          >
            <Link
              to="/drive/ibiza"
              className="inline-flex items-center gap-2 font-label text-primary-foreground/50 hover:text-primary-foreground transition-colors duration-500 mb-6"
            >
              <ArrowLeft size={14} />
              {t("Back to fleet", "Terug naar vloot")}
            </Link>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wide uppercase mb-2">
              {car.name}
            </h1>
            <p className="font-label text-bronze text-sm md:text-base">
              {car.price}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Details */}
      <section className="section-spacing bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-28">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease }}
            >
              <span className="font-label text-bronze block mb-6">{t("About This Vehicle", "Over Dit Voertuig")}</span>
              <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] mb-10">
                {car.description}
              </p>

              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="flex items-start gap-3">
                  <Users size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">{t("Passengers", "Passagiers")}</span>
                    <span className="font-body text-foreground text-sm">{getSpecValue("Passengers")}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Zap size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">{t("Engine", "Motor")}</span>
                    <span className="font-body text-foreground text-sm">{getSpecValue("Engine")}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Gauge size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">{t("Power", "Vermogen")}</span>
                    <span className="font-body text-foreground text-sm">{getSpecValue("Power")}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Timer size={18} className="text-bronze mt-0.5 shrink-0" />
                  <div>
                    <span className="font-label text-foreground/40 block text-[10px] mb-1">0–100 km/h</span>
                    <span className="font-body text-foreground text-sm">{getSpecValue("0–100 km/h")}</span>
                  </div>
                </div>
              </div>

              <div>
                <span className="font-label text-foreground/40 block text-[10px] mb-2">{t("Top Speed", "Topsnelheid")}</span>
                <span className="font-body text-foreground text-sm">{getSpecValue("Top Speed")}</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.15, ease }}
            >
              <span className="font-label text-bronze block mb-6">{t("Rental · Season 2026", "Verhuur · Seizoen 2026")}</span>

              <div className="border border-border/30 mb-10">
                <div className="p-8 text-center">
                  <span className="font-label text-foreground/40 block text-[10px] mb-2">{t("Daily Rate", "Dagtarief")}</span>
                  <span className="font-display-medium text-foreground text-3xl md:text-4xl">{car.price.replace("From ", "").replace("Vanaf ", "")}</span>
                </div>
                <div className="border-t border-border/30 p-4">
                  <p className="font-body text-muted-foreground/60 text-xs italic text-center">
                    {t(
                      "*Prices may vary depending on season and rental duration. Contact us for a tailored quote.",
                      "*Prijzen kunnen variëren afhankelijk van seizoen en huurperiode. Neem contact op voor een offerte op maat."
                    )}
                  </p>
                </div>
              </div>

              <div className="mb-12">
                <span className="font-label text-foreground/40 block text-[10px] mb-4">{t("Included", "Inbegrepen")}</span>
                <div className="flex flex-wrap gap-3">
                  {[
                    t("Delivery & Collection", "Bezorging & Ophaling"),
                    t("Full Insurance", "Volledige Verzekering"),
                    t("24/7 Support", "24/7 Ondersteuning"),
                    t("Unlimited Mileage", "Onbeperkte Kilometers"),
                  ].map((item) => (
                    <span
                      key={item}
                      className="font-body text-foreground text-xs border border-border/30 px-4 py-2"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border-t border-border/20 pt-10">
                <h3 className="font-display-medium text-lg text-foreground tracking-wide uppercase mb-4">
                  {t("Interested in this vehicle?", "Geïnteresseerd in dit voertuig?")}
                </h3>
                <p className="font-body text-muted-foreground text-sm leading-[1.8] mb-6">
                  {t(
                    "Send us a message with your preferred dates and delivery location. We'll confirm availability and arrange every detail.",
                    "Stuur ons een bericht met uw gewenste data en afleverlocatie. Wij bevestigen de beschikbaarheid en regelen elk detail."
                  )}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://wa.me/message/2XEKJ5STR2IZJ1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500 text-center"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={`mailto:info@arzanigroupworldwide.com?subject=Vehicle Inquiry: ${car.name}`}
                    className="inline-block px-8 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500 text-center"
                  >
                    {t("Email Us", "E-mail Ons")}
                  </a>
                </div>
                <div className="mt-8 border-t border-border/10 pt-6">
                  <p className="font-body text-muted-foreground/50 text-xs leading-[1.9] italic">
                    {t(
                      "Vehicles can be delivered to your villa, hotel, or the airport. A deposit is required to confirm the reservation.",
                      "Voertuigen kunnen worden afgeleverd bij uw villa, hotel of de luchthaven. Een aanbetaling is vereist om de reservering te bevestigen."
                    )}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-4xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">{t("How It Works", "Hoe Het Werkt")}</span>
            <h2 className="font-display text-3xl md:text-4xl text-primary-foreground tracking-wide uppercase">
              {t("Vehicle Requests", "Voertuig Aanvragen")}
            </h2>
            <p className="font-body text-primary-foreground/40 text-sm mt-4">
              {t("A simple process, designed with ease in mind.", "Een eenvoudig proces, ontworpen met gemak in gedachten.")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              {
                step: "1",
                title: t("Choose your vehicle", "Kies uw voertuig"),
                text: t(
                  "Browse our collection or tell us what you're looking for. Our team will help you find the perfect match.",
                  "Bekijk onze collectie of vertel ons wat u zoekt. Ons team helpt u de perfecte match te vinden."
                ),
              },
              {
                step: "2",
                title: t("Share your dates", "Deel uw data"),
                text: t(
                  "Let us know your preferred dates and pick-up location. We'll check availability and arrange delivery.",
                  "Laat ons uw gewenste data en ophaallocatie weten. Wij controleren de beschikbaarheid en regelen de levering."
                ),
              },
              {
                step: "3",
                title: t("Enjoy the drive", "Geniet van de rit"),
                text: t(
                  "Your vehicle arrives fully prepared and ready. Take the keys, and let Ibiza's roads set the tone.",
                  "Uw voertuig komt volledig voorbereid en klaar aan. Neem de sleutels en laat de wegen van Ibiza de toon zetten."
                ),
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.15, ease }}
              >
                <span className="font-display text-5xl text-primary-foreground/10 block mb-4">
                  {item.step}
                </span>
                <h3 className="font-display-medium text-base text-primary-foreground tracking-wide uppercase mb-4">
                  {item.title}
                </h3>
                <p className="font-body text-primary-foreground/50 text-sm leading-[1.9]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Browse more */}
      <section className="py-16 bg-background">
        <div className="container-luxury text-center">
          <Link
            to="/drive/ibiza"
            className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
          >
            {t("Browse Full Fleet", "Bekijk Volledige Vloot")}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CarDetail;
